import { action, computed, makeObservable, observable } from 'mobx';

import { getLiveLocks, type LiveLock, type LiveLocksResponse } from 'api/locks/endpoints/getLiveLocks';
import { answerOf, MobxQuery, type QueryValue } from 'mobx/MobxQuery';
import { acceptDegraded } from 'validation';

/** The page-level filters, and the one URL-driven parameter. */
export interface LiveLocksFilters {
  readonly zoneId: number | null;
  readonly search: string;
  readonly includeIdle: boolean;
  readonly focusLockId: number | null;
}

/**
 * Behaviour for the Live Locks page.
 *
 * The page read is a {@link MobxQuery}, re-asked whenever a filter moves, and
 * the single source of truth for which locks are listed and what they look
 * like. The page has no actions, so nothing but a reload ever changes a card,
 * and a card needs no model of its own: it renders a {@link LiveLock} from the
 * answer, and reads its collapse state from here.
 */
export class LiveLocksModel {
  private _expandedByDefault = true;
  private _focusLockId: number | null = null;
  private _includeIdle = false;
  private readonly _query: MobxQuery<LiveLocksFilters, LiveLocksResponse>;
  private _search = '';
  /**
   * Lock ids whose collapse state is flipped relative to {@link _expandedByDefault}.
   * A map, not a set: MobX tracks a map's `has` per key, so a card observes its
   * own entry and a toggle re-renders that card alone.
   */
  private readonly _toggled = observable.map<number, true>();
  private _zoneId: number | null = null;

  /**
   * @param focusLockId The URL's focus as the page opens. Seeded here rather
   * than set after the first render, so the first request already carries it
   * and a deep link to an idle lock never asks for the page without it first.
   */
  constructor(focusLockId: number | null) {
    this._focusLockId = focusLockId;
    makeObservable<LiveLocksModel, '_expandedByDefault' | '_focusLockId' | '_includeIdle' | '_search' | '_zoneId'>(this, {
      _expandedByDefault: observable,
      _focusLockId: observable,
      _includeIdle: observable,
      _search: observable,
      _zoneId: observable,
      locks: computed,
      collapseAll: action,
      expandAll: action,
      setFocusLockId: action,
      setIncludeIdle: action,
      setSearch: action,
      setZoneId: action,
      toggleCollapsed: action,
    });

    this._query = new MobxQuery<LiveLocksFilters, LiveLocksResponse>({
      params: () => ({
        zoneId: this._zoneId,
        search: this._search,
        includeIdle: this._includeIdle,
        focusLockId: this._focusLockId,
      }),
      fetch: async (params) =>
        acceptDegraded(
          await getLiveLocks({
            ...(params.zoneId === null ? {} : { zone_id: params.zoneId }),
            search: params.search,
            include_idle: params.includeIdle,
            // The focused lock comes back first whether or not it qualifies:
            // "nothing holds it" answers the question an operator arrived
            // with, where an empty page does not.
            ...(params.focusLockId === null ? {} : { lock_ids: [params.focusLockId] }),
          })
        ),
      // Search is keystroke-shaped; the other controls share the params object
      // and so the same delay. The page goes `stale` immediately either way.
      debounceMs: 300,
    });
  }

  // ── Accessors ─────────────────────────────────────────────────────────────

  /** When the shown answer was read; null before the first one lands. */
  get asOf(): string | null {
    return answerOf(this._query.value)?.value.as_of ?? null;
  }

  /** The focused lock's card, when this answer has it. */
  get focusedLock(): LiveLock | null {
    return this.locks.find((lock) => lock.lock_id === this._focusLockId) ?? null;
  }

  get focusLockId(): number | null {
    return this._focusLockId;
  }

  get includeIdle(): boolean {
    return this._includeIdle;
  }

  /**
   * True before the first answer lands, including before the query has armed;
   * a spinner rather than an empty page.
   */
  get isLoading(): boolean {
    const { kind } = this._query.value;
    return kind === 'uninitialized' || kind === 'loading';
  }

  /** True while a newer answer is on its way; the cards on screen are dimmed, not blanked. */
  get isStale(): boolean {
    return this._query.value.kind === 'stale';
  }

  /** In the backend's order: the focused lock first, then by zone and name. */
  get locks(): readonly LiveLock[] {
    return answerOf(this._query.value)?.value.locks ?? [];
  }

  get pageError(): string | null {
    const value: QueryValue<LiveLocksResponse> = this._query.value;
    return value.kind === 'error' ? value.error.message : null;
  }

  get search(): string {
    return this._search;
  }

  /** The row cap was hit — the page must say so rather than imply completeness. */
  get truncated(): boolean {
    return answerOf(this._query.value)?.value.truncated ?? false;
  }

  get zoneId(): number | null {
    return this._zoneId;
  }

  // ── Methods ───────────────────────────────────────────────────────────────

  collapseAll = (): void => {
    this._expandedByDefault = false;
    this._toggled.clear();
  };

  dispose = (): void => {
    this._query.dispose();
  };

  expandAll = (): void => {
    this._expandedByDefault = true;
    this._toggled.clear();
  };

  /**
   * Whether one card is collapsed. Expand-all / collapse-all is a flag flip
   * plus an empty toggle set, so it applies to cards that have never been
   * rendered.
   */
  isCollapsed = (lockId: number): boolean => (this._expandedByDefault ? this._toggled.has(lockId) : !this._toggled.has(lockId));

  refreshPage = async (): Promise<void> => {
    await this._query.refresh();
  };

  setFocusLockId = (lockId: number | null): void => {
    this._focusLockId = lockId;
  };

  setIncludeIdle = (include: boolean): void => {
    this._includeIdle = include;
  };

  setSearch = (search: string): void => {
    this._search = search;
  };

  setZoneId = (zoneId: number | null): void => {
    this._zoneId = zoneId;
  };

  toggleCollapsed = (lockId: number): void => {
    if (this._toggled.has(lockId)) {
      this._toggled.delete(lockId);
    } else {
      this._toggled.set(lockId, true);
    }
  };
}
